import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { backendUrl } from '../config/api';

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user,    setUser]    = useState(null);
    const [loading, setLoading] = useState(true);

    // Check existing session on mount
    useEffect(() => {
        axios.get(`${backendUrl}/api/auth/me`)
            .then(r => setUser(r.data.user))
            .catch(() => setUser(null))
            .finally(() => setLoading(false));
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const r = await axios.get(`${backendUrl}/api/auth/me`);
            setUser(r.data.user);
            return r.data.user;
        } catch { setUser(null); return null; }
    }, []);

    const login = useCallback(async (username, password) => {
        const r = await axios.post(`${backendUrl}/api/auth/login`, { username, password });
        setUser(r.data.user);
        return r.data.user;
    }, []);

    const logout = useCallback(async () => {
        await axios.post(`${backendUrl}/api/auth/logout`);
        setUser(null);
    }, []);

    const hasApiPermission = useCallback(permissionKey => {
        if (!user) return false;
        if (user.role === 'admin') return true;
        return Boolean(user.apiPermissions?.[permissionKey]);
    }, [user]);

    // Memoized: a fresh object every render would force every useAuth()
    // consumer (navbar, sidebar, dashboard, guards…) to re-render whenever any
    // parent of AuthProvider re-renders.
    const value = useMemo(() => ({
        user,
        loading,
        login,
        logout,
        refreshUser,
        isAdmin: user?.role === 'admin',
        isApiManager: user?.role === 'api_manager' || user?.role === 'admin',
        isLoggedIn: !!user,
        hasApiPermission,
        canCreateGames: hasApiPermission('createGame'),
    }), [user, loading, login, logout, refreshUser, hasApiPermission]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
